import type { NotificationPriority, NotificationType } from './channel-types';

/**
 * Input for the dispatch service's `emit()` method.
 * Any backend service can construct this to trigger a notification.
 */
export interface EmitNotificationInput {
  /** Event type identifier (e.g. `plan_published`, `shift_assigned`). */
  eventType: string;

  /** Source entity type (e.g. `plan`, `shift`). */
  sourceType: string;

  /** Source entity ID (nullable for system-wide events). */
  sourceId?: string | null;

  /** `auth.users.id` values (NOT Person IDs) who should receive this notification. */
  recipients: string[];

  /** Frozen event payload — stored immutably for resend capability. */
  payload: Record<string, unknown>;

  /** Template for the per-recipient notification records. */
  template: NotificationTemplate;
}

/**
 * Template for creating per-recipient notification records.
 */
export interface NotificationTemplate {
  /** Notification title (can be an i18n key or literal). */
  title: string;
  /** Notification body message (can be an i18n key or literal). */
  message: string;
  /** Visual type/severity. */
  type: NotificationType;
  /** Delivery priority. */
  priority: NotificationPriority;
  /** Optional grouping category (e.g. `planning`, `timetracking`). */
  category?: string;
  /** Optional structured metadata (action URLs, entity refs, deep links). */
  metadata?: Record<string, unknown> | null;
}

/**
 * Data payload for BullMQ notification delivery jobs.
 * Submitted to the NOTIFICATIONS queue per delivery record.
 */
export interface NotificationDeliveryJobData extends Record<string, unknown> {
  deliveryId: string;
  notificationId: string;
  channel: string;
  tenantId?: string;
  databaseUrl?: string;
}

/**
 * Result of a dispatch operation.
 */
export interface DispatchResult {
  /** ID of the created event record. */
  eventId: string;
  /** IDs of created notification records (one per recipient). */
  notificationIds: string[];
  /** IDs of created delivery records (one per notification per eligible channel). */
  deliveryIds: string[];
}
